<template>
  <v-navigation-drawer
    v-model="drawerStore.isOpen"
    :rail="drawerStore.isRail"
    app
    width="280"
    tag="nav"
    :aria-label="t('navigation.mainNavigation')"
    class="sidebar-soft"
  >
    <div class="sidebar-content">
      <v-list density="compact" class="sidebar-nav">
        <template v-for="(section, sIdx) in menuSections" :key="section.label">
          <v-divider v-if="sIdx > 0" class="my-1" />
          <v-list-subheader v-if="!drawerStore.isRail" class="section-subheader">
            {{ t(section.label) }}
          </v-list-subheader>

          <v-tooltip
            v-for="item in section.items"
            :key="item.path"
            :text="item.title"
            location="end"
            :disabled="!drawerStore.isRail"
          >
            <template #activator="{ props: tooltipProps }">
              <v-list-item
                v-bind="tooltipProps"
                :to="item.path"
                :active="isActive(item.path)"
                @keydown="handleNavigationKeydown"
              >
                <template #prepend>
                  <v-icon>{{ item.icon }}</v-icon>
                </template>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
            </template>
          </v-tooltip>
        </template>

        <v-divider class="my-1" />

        <v-list-group v-model="isSystemOpen">
          <template #activator="{ props }">
            <v-tooltip
              :text="t('navigation.sectionSystem')"
              location="end"
              :disabled="!drawerStore.isRail"
            >
              <template #activator="{ props: tooltipProps }">
                <v-list-item
                  v-bind="{ ...props, ...tooltipProps }"
                  prepend-icon="mdi-cog-outline"
                  :title="t('navigation.sectionSystem')"
                />
              </template>
            </v-tooltip>
          </template>

          <v-tooltip :text="t('navigation.rules')" location="end" :disabled="!drawerStore.isRail">
            <template #activator="{ props: tooltipProps }">
              <v-list-item
                v-bind="tooltipProps"
                to="/rules"
                :active="isActive('/rules')"
                prepend-icon="mdi-shield-check"
                :title="t('navigation.rules')"
                @keydown="handleNavigationKeydown"
              />
            </template>
          </v-tooltip>

          <v-tooltip
            :text="t('navigation.settings')"
            location="end"
            :disabled="!drawerStore.isRail"
          >
            <template #activator="{ props: tooltipProps }">
              <v-list-item
                v-bind="tooltipProps"
                to="/settings"
                :active="isActive('/settings')"
                prepend-icon="mdi-cog"
                :title="t('navigation.settings')"
                @keydown="handleNavigationKeydown"
              />
            </template>
          </v-tooltip>
        </v-list-group>
      </v-list>
    </div>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDrawerStore } from '@/stores/drawer';

interface MenuItem {
  title: string;
  path: string;
  icon: string;
}

interface MenuSection {
  label: string;
  items: MenuItem[];
}

const route = useRoute();
const { t } = useI18n();
const drawerStore = useDrawerStore();
const currentFocusIndex = ref(0);

const menuSections = computed<MenuSection[]>(() => [
  {
    label: 'navigation.sectionMain',
    items: [{ title: t('navigation.dashboard'), path: '/', icon: 'mdi-view-dashboard' }],
  },
  {
    label: 'navigation.sectionManagement',
    items: [
      { title: t('navigation.users'), path: '/users', icon: 'mdi-account-group' },
      { title: t('navigation.resources'), path: '/resources', icon: 'mdi-hospital-building' },
      { title: t('navigation.appointments'), path: '/appointments', icon: 'mdi-calendar' },
    ],
  },
  {
    label: 'navigation.sectionCommunication',
    items: [
      {
        title: t('navigation.scheduledMessages'),
        path: '/scheduled-messages',
        icon: 'mdi-clock-outline',
      },
      { title: t('navigation.chat'), path: '/chat', icon: 'mdi-chat' },
    ],
  },
  {
    label: 'navigation.sectionAnalysis',
    items: [{ title: t('navigation.reports'), path: '/reports', icon: 'mdi-chart-bar' }],
  },
]);

const isSystemOpen = ref(route.path.startsWith('/rules') || route.path.startsWith('/settings'));

const isActive = (path: string) => {
  if (path === '/') return route.path === '/';
  return route.path === path || route.path.startsWith(`${path}/`);
};

const handleNavigationKeydown = (event: KeyboardEvent) => {
  const target = event.currentTarget as HTMLElement;
  const listItems = Array.from(
    target.closest('.sidebar-nav')?.querySelectorAll('.v-list-item') ?? []
  );
  const currentIndex = listItems.indexOf(target);
  if (currentIndex === -1) return;

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    const nextIndex = currentIndex < listItems.length - 1 ? currentIndex + 1 : 0;
    (listItems[nextIndex] as HTMLElement).focus();
    currentFocusIndex.value = nextIndex;
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : listItems.length - 1;
    (listItems[prevIndex] as HTMLElement).focus();
    currentFocusIndex.value = prevIndex;
  }
};
</script>

<style scoped>
.sidebar-soft {
  background: linear-gradient(180deg, #f8f9fb 0%, #ffffff 100%) !important;
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
}

.section-subheader {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.45);
  min-height: 28px;
  padding: 4px 12px 0;
}

.sidebar-nav :deep(.v-list-item) {
  border-radius: 0;
  margin-bottom: 1px;
  padding-inline: 12px;
  transition: background 0.2s ease, border-left 0.2s ease;
}

.sidebar-nav :deep(.v-list-group__items .v-list-item) {
  padding-inline-start: 12px !important;
}

.sidebar-nav :deep(.v-list-item:hover) {
  background: rgba(var(--v-theme-primary), 0.06) !important;
}

.sidebar-nav :deep(.v-list-item.v-list-item--active) {
  background: rgba(var(--v-theme-primary), 0.14) !important;
  border-left: 3px solid rgb(var(--v-theme-primary));
}

.sidebar-nav :deep(.v-list-item.v-list-item--active .v-list-item-title) {
  font-weight: 600;
}

.sidebar-nav :deep(.v-list-item-title) {
  font-size: 0.835rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.sidebar-nav :deep(.v-list-item) {
  justify-content: flex-start;
}

.sidebar-nav :deep(.v-list-item .v-list-item__content) {
  text-align: left;
}

.sidebar-nav :deep(.v-list-item .v-list-item__prepend) {
  margin-inline-end: 8px;
}
</style>
